/**
 * Autocomplete Component
 *
 * Shows dropdown with matching posts as user types in search input.
 * Handles focus (preload), input (debounced search), dropdown display,
 * click-outside-to-close, and mousedown navigation.
 */

if (typeof FN == 'undefined') FN = {};

/**
 * Debounce utility function
 * Delays execution until user stops typing
 * @param {Function} func - Function to debounce
 * @param {number} delay - Milliseconds to wait (default 300)
 * @returns {Function} Debounced function
 */
function debounce(func, delay = 300) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

FN.autocomplete = {
  // State variables
  searchInput: null,
  dropdown: null,
  isDropdownVisible: false,
  selectedIndex: -1, // -1 means no selection, focus stays in input

  /**
   * Initialize autocomplete component
   * Attaches to #search-input if present on page
   */
  init: function() {
    // Get search input element
    this.searchInput = document.getElementById('search-input');

    // Not all pages have search - exit gracefully
    if (!this.searchInput) {
      return;
    }

    // Create dropdown element
    this.dropdown = this.createDropdown();

    // Attach event listeners
    this.attachListeners();
  },

  /**
   * Create dropdown element and insert after search input
   * @returns {HTMLElement} Created dropdown element
   */
  createDropdown: function() {
    const dropdown = document.createElement('ul');
    dropdown.id = 'search-autocomplete-results';
    dropdown.className = 'autocomplete-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('hidden', '');

    // Insert after search input (inside .search-section)
    this.searchInput.parentElement.appendChild(dropdown);

    return dropdown;
  },

  /**
   * Show dropdown with search results
   * @param {Array} results - Array of matching posts with highlightedTitle
   * @param {string} query - Original search query
   */
  showDropdown: function(results, query) {
    // Clear previous content
    this.dropdown.innerHTML = '';
    this.selectedIndex = -1; // Reset selection

    // Hide if no results
    if (results.length === 0) {
      this.hideDropdown();
      return;
    }

    // Render each result as option
    results.forEach(result => {
      const li = document.createElement('li');
      li.className = 'autocomplete-option';
      li.setAttribute('role', 'option');

      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.innerHTML = result.highlightedTitle;

      li.appendChild(anchor);
      this.dropdown.appendChild(li);
    });

    // Add "View all results" option
    const viewAllLi = document.createElement('li');
    viewAllLi.className = 'autocomplete-option autocomplete-option--view-all';
    viewAllLi.setAttribute('role', 'option');

    const viewAllAnchor = document.createElement('a');
    const searchPageURL = FN.searchPageURL || '/search';
    viewAllAnchor.href = `${searchPageURL}?q=${encodeURIComponent(query)}`;
    viewAllAnchor.textContent = `View all results for "${query}"`;

    viewAllLi.appendChild(viewAllAnchor);
    this.dropdown.appendChild(viewAllLi);

    // Show dropdown
    this.dropdown.removeAttribute('hidden');
    this.isDropdownVisible = true;
  },

  /**
   * Hide dropdown
   */
  hideDropdown: function() {
    this.dropdown.setAttribute('hidden', '');
    this.isDropdownVisible = false;
    this.selectedIndex = -1;
  },

  /**
   * Update visual selection indicator
   */
  updateSelection: function() {
    const options = this.dropdown.querySelectorAll('.autocomplete-option');

    // Remove selection from all options
    options.forEach((option, index) => {
      if (index === this.selectedIndex) {
        option.classList.add('selected');
        option.querySelector('a').setAttribute('aria-selected', 'true');
      } else {
        option.classList.remove('selected');
        option.querySelector('a').setAttribute('aria-selected', 'false');
      }
    });
  },

  /**
   * Navigate to next option
   */
  selectNext: function() {
    const optionCount = this.dropdown.querySelectorAll('.autocomplete-option').length;
    if (this.selectedIndex < optionCount - 1) {
      this.selectedIndex++;
      this.updateSelection();
    }
  },

  /**
   * Navigate to previous option
   */
  selectPrevious: function() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      this.updateSelection();
    } else if (this.selectedIndex === 0) {
      // Wrap back to input
      this.selectedIndex = -1;
      this.updateSelection();
    }
  },

  /**
   * Navigate to selected option (or submit form if no selection)
   */
  selectCurrent: function() {
    if (this.selectedIndex >= 0) {
      const options = this.dropdown.querySelectorAll('.autocomplete-option');
      const selectedOption = options[this.selectedIndex];
      if (selectedOption) {
        const link = selectedOption.querySelector('a');
        if (link) {
          window.location.href = link.href;
        }
      }
    }
    // If selectedIndex is -1, let the form submit naturally
  },

  /**
   * Attach all event listeners
   */
  attachListeners: function() {
    const self = this;

    // Focus event - preload index
    this.searchInput.addEventListener('focus', function() {
      // Fire and forget - preload for fast search
      FN.searchIndex.load();
    });

    // Input event - debounced search
    const debouncedSearch = debounce(function(e) {
      const query = e.target.value.trim();

      // Require 3+ characters
      if (query.length < 3) {
        self.hideDropdown();
        return;
      }

      // Search and display results
      const results = FN.searchIndex.search(query);
      self.showDropdown(results, query);
    }, 300);

    this.searchInput.addEventListener('input', debouncedSearch);

    // Mousedown event on dropdown (fires before blur)
    // Allows click navigation before blur closes dropdown
    this.dropdown.addEventListener('mousedown', function(e) {
      // If clicking anchor, let default navigation happen
      if (e.target.tagName === 'A') {
        return;
      }
      // Prevent blur event from firing
      e.preventDefault();
    });

    // Click outside to close
    document.addEventListener('click', function(e) {
      // If click is outside search section, hide dropdown
      const searchSection = self.searchInput.closest('.search-section');
      if (!searchSection.contains(e.target)) {
        self.hideDropdown();
      }
    });

    // Blur event on search input (delayed to allow mousedown to fire first)
    this.searchInput.addEventListener('blur', function() {
      // Delay to allow mousedown to fire before blur closes dropdown
      setTimeout(() => {
        self.hideDropdown();
      }, 200);
    });

    // Keydown event for keyboard navigation
    this.searchInput.addEventListener('keydown', function(e) {
      // Only handle keys when dropdown is visible
      if (!self.isDropdownVisible) {
        return;
      }

      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault(); // Prevent cursor move
          self.selectNext();
          break;

        case 'ArrowUp':
          e.preventDefault(); // Prevent cursor move
          self.selectPrevious();
          break;

        case 'Enter':
          // Only prevent default if an option is selected
          if (self.selectedIndex >= 0) {
            e.preventDefault(); // Prevent form submission
            self.selectCurrent();
          }
          // Otherwise let form submit naturally
          break;

        case 'Escape':
          e.preventDefault();
          self.hideDropdown();
          break;
      }
    });
  }
};

// Initialize on document ready
document.addEventListener('DOMContentLoaded', function() {
  FN.autocomplete.init();
});
